import React, { useEffect, useState } from 'react';
import { Button, Card, Flex, Spin, Typography } from 'antd';
import { useAuth } from '../auth/AuthContext';
import { hasTotpMfa } from '../services/mfaService';
import MfaSetup from './MfaSetup';

const { Text } = Typography;

type State = 'checking' | 'enrol' | 'ok' | 'error';

// KAN-163: the pool runs MFA=OPTIONAL so admins can reset a lost authenticator.
// This gate makes enrolment mandatory anyway: nothing renders until the
// signed-in user has TOTP enabled. Runs once per session after sign-in.
const RequireMfa: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authenticated, user, logout } = useAuth();
  const [state, setState] = useState<State>('checking');

  useEffect(() => {
    if (!authenticated) return;
    let cancelled = false;
    hasTotpMfa()
      .then(ok => {
        if (!cancelled) setState(ok ? 'ok' : 'enrol');
      })
      .catch(err => {
        // Typically a pre-KAN-163 session whose access token lacks the
        // user.admin scope; a fresh sign-in fixes it.
        console.warn('GetUser failed:', err);
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [authenticated]);

  if (state === 'ok') return <>{children}</>;

  return (
    <Flex
      vertical
      justify="center"
      align="center"
      gap={16}
      style={{ minHeight: '100vh', padding: 24 }}
    >
      {state === 'checking' && <Spin size="large" />}
      {state === 'error' && (
        <>
          <Text type="danger">Could not verify your sign-in security settings.</Text>
          <Button type="primary" onClick={logout}>
            Sign in again
          </Button>
        </>
      )}
      {state === 'enrol' && (
        <Card title="Set up your authenticator app" style={{ maxWidth: 480, width: '100%' }}>
          <MfaSetup email={user?.email || ''} onDone={() => setState('ok')} />
        </Card>
      )}
    </Flex>
  );
};

export default RequireMfa;
